import React, { useEffect, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { detectUserCurrency, loadFxRates, formatLocalPreview, flagFor } from "@/lib/currency";

const API = `${BACKEND_URL}/api`;

export default function PaywallModal({ open, onOpenChange }) {
  const { user, API: apiFromCtx } = useAuth();
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState("pro_monthly");
  const [rates, setRates] = useState(null);
  const [localCurrency, setLocalCurrency] = useState("CAD");

  useEffect(() => {
    if (!open) return;
    setLocalCurrency(detectUserCurrency());
    loadFxRates().then(r => r && setRates(r));
  }, [open]);

  const monthlyPreview = formatLocalPreview(4.99, rates, localCurrency);
  const yearlyPreview = formatLocalPreview(49.90, rates, localCurrency);

  const start = async () => {
    if (!user) { toast.error("Please log in first"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        lookup_key: plan,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121620] border-[#00F0FF]/40 text-white sm:max-w-lg" data-testid="paywall-modal">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-3xl">
            Unlock <span className="text-[#00F0FF]">PRO</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="text-sm text-[#94A3B8]">Save mixes, unlimited AI recommendations, cloud sync.</div>

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setPlan("pro_monthly")}
              data-testid="paywall-plan-monthly"
              className={`p-4 rounded-lg border-2 text-left transition-colors ${plan === "pro_monthly" ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-[#2A354B] hover:border-[#2A354B]"}`}
            >
              <div className="text-xs font-mono uppercase tracking-widest text-[#94A3B8]">Monthly</div>
              <div className="font-display font-black text-2xl">CA$4.99<span className="text-sm text-[#94A3B8]">/mo</span></div>
              {monthlyPreview && (
                <div className="text-[10px] font-mono text-[#94A3B8] mt-0.5" data-testid="paywall-monthly-local">{flagFor(localCurrency)} ≈ {monthlyPreview}</div>
              )}
              <div className="text-[10px] font-mono text-[#00FF66] mt-1">7-day free trial</div>
            </button>
            <button
              onClick={() => setPlan("pro_yearly")}
              data-testid="paywall-plan-yearly"
              className={`p-4 rounded-lg border-2 text-left transition-colors ${plan === "pro_yearly" ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-[#2A354B] hover:border-[#2A354B]"}`}
            >
              <div className="text-xs font-mono uppercase tracking-widest text-[#00FF66]">Yearly · Save 17%</div>
              <div className="font-display font-black text-2xl">$49<span className="text-sm text-[#94A3B8]">/yr</span></div>
              {yearlyPreview && (
                <div className="text-[10px] font-mono text-[#94A3B8] mt-0.5" data-testid="paywall-yearly-local">{flagFor(localCurrency)} ≈ {yearlyPreview}</div>
              )}
              <div className="text-[10px] font-mono text-[#00FF66] mt-1">7-day free trial</div>
            </button>
          </div>

          <ul className="space-y-2 text-sm">
            {["Save & load unlimited mix sessions", "Unlimited AI harmonic recommendations", "Cloud sync across devices", "Cancel anytime · Stripe secure checkout"].map(x => (
              <li key={x} className="flex items-center gap-2"><Check className="w-4 h-4 text-[#00F0FF]"/> {x}</li>
            ))}
          </ul>

          <Button data-testid="stripe-checkout-button" onClick={start} disabled={busy || !user} className="w-full bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-black uppercase tracking-widest py-6">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : (user ? "Continue to Stripe Checkout" : "Log in to upgrade")}
          </Button>

          {localCurrency !== "CAD" && (
            <div className="text-[10px] text-center text-[#94A3B8] font-mono" data-testid="paywall-fx-note">
              Stripe charges in CAD only · your bank converts to {localCurrency} at their FX rate
            </div>
          )}
          <div className="text-[11px] text-center text-[#94A3B8] font-mono">Test card: 4242 4242 4242 4242 · any future date · any CVC</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
